/**
 * Server-side data fetchers for Next.js server components.
 *
 * These mirror the client-side hooks (useTriggers, useActions, etc.)
 * but use plain fetch() calls suitable for server components and
 * generateStaticParams / generateMetadata.
 *
 * All functions throw if V2_ENDPOINT is not configured.
 */
import { CACHE_ENDPOINT, V2_ENDPOINT } from "@/consts";
import { LONG_CACHE, MEDIUM_CACHE } from "@/lib/api/cache";
import type { X402Endpoint } from "@/hooks/useX402Endpoints";
import { shortIdOf } from "@/lib/x402/endpointSlug";
import { templateCacheTag, TEMPLATES_CACHE_TAG } from "@/lib/templates/cacheTags";
import { VARIABLE_REF_RE } from "@/lib/workflow/sanitize";
import { cache } from "react";

// -----------------------------------------------------------------------
// Types (matching the client-side ActionTriggerDefinition)
// -----------------------------------------------------------------------

export interface ServerActionTriggerDef {
  type: string;
  name: string;
  description: string;
  platform?: string;
  icon?: string;
  logoUrl?: string;
  category?: "action" | "trigger" | "logic";
  payloadSchema?: Record<string, unknown>;
  resultSchema?: Record<string, unknown>;
  connector?: { type: string };
  tags?: string[];
}

export interface ServerConnectorType {
  type: string;
  name: string;
  description?: string;
  logoUrl?: string;
  categories?: string[];
  tags?: string[];
  provider?: string;
  informationUrl?: string;
}

export interface ServerApp {
  slug: string;
  name: string;
  description: string;
  logoUrl?: string;
  categories?: string[];
  tags?: string[];
  triggerCount: number;
  actionCount: number;
  totalIntegrations: number;
  isSpecialCategory?: boolean;
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

export function requireV2Endpoint(): string {
  if (!V2_ENDPOINT && !process.env.CI) {
    throw new Error(
      "[server-fetchers] NEXT_PUBLIC_V2_ENDPOINT is not configured. " + "Run `doppler setup` or set the env var.",
    );
  }
  return V2_ENDPOINT;
}

async function serverFetch<T>(path: string): Promise<T> {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return [] as unknown as T;

  try {
    const res = await fetch(`${endpoint}${path}`, LONG_CACHE);

    if (!res.ok) {
      console.error(`[server-fetchers] ${res.status} ${res.statusText} — GET ${endpoint}${path}`);
      return [] as unknown as T;
    }

    const body = await res.json();
    return (body.data ?? body) as T;
  } catch (err) {
    console.error(`[server-fetchers] Could not reach API — GET ${endpoint}${path} — ${(err as Error).message}`);
    return [] as unknown as T;
  }
}

// -----------------------------------------------------------------------
// Fetchers
// -----------------------------------------------------------------------

/**
 * Fetch triggers, optionally filtered by tags.
 * Accepts a comma-separated string (not an array) so React cache() can
 * deduplicate via Object.is() between generateMetadata and the page component.
 */
export const fetchServerTriggers = cache(async (tags?: string): Promise<ServerActionTriggerDef[]> => {
  const params = tags ? `?tags=${encodeURIComponent(tags)}` : "";
  return serverFetch<ServerActionTriggerDef[]>(`/v1/triggers${params}`);
});

/** @see fetchServerTriggers — same cache-friendly string-arg pattern */
export const fetchServerActions = cache(async (tags?: string): Promise<ServerActionTriggerDef[]> => {
  const params = tags ? `?tags=${encodeURIComponent(tags)}` : "";
  return serverFetch<ServerActionTriggerDef[]>(`/v1/actions${params}`);
});

export async function fetchServerLogicActions(): Promise<ServerActionTriggerDef[]> {
  return serverFetch<ServerActionTriggerDef[]>("/v1/logic-actions");
}

export async function fetchServerConnectorTypes(): Promise<ServerConnectorType[]> {
  return serverFetch<ServerConnectorType[]>("/v1/connector-types");
}

export interface ServerTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  weight: number;
  count: number;
  imageUrl?: string;
  categories?: string[];
}

export const fetchServerTags = cache(async (): Promise<ServerTag[]> => {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return [];

  try {
    const res = await fetch(`${endpoint}/v1/tags?limit=1000&scope=actions&scope=triggers`, LONG_CACHE);
    if (!res.ok) return [];
    const body = await res.json();
    const data = body.data ?? body;
    return data.items ?? data;
  } catch {
    return [];
  }
});

export interface ServerNodeStatistics {
  nodeType: "trigger" | "action";
  nodeId: string;
  workflowsCount: number;
  totalExecutions: number;
  successRate: number | null;
  averageLatencyMs: number | null;
}

export async function fetchServerStatistics(): Promise<Record<string, ServerNodeStatistics>> {
  const data = await serverFetch<Record<string, ServerNodeStatistics>>("/v1/statistics");
  // serverFetch returns [] on error, which is wrong for Record types
  return Array.isArray(data) ? {} : data;
}

// -----------------------------------------------------------------------
// App derivation (mirrors hooks/useApps.ts logic)
// -----------------------------------------------------------------------

export function normalizeToSlug(type: string): string {
  return type.replace(":", "-").toLowerCase();
}

export function getAppSlug(item: ServerActionTriggerDef): string | null {
  if (item.platform) return item.platform.toLowerCase();
  if (item.connector?.type) return normalizeToSlug(item.connector.type);
  return null;
}

function formatName(slug: string): string {
  return slug
    .split("-")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Special category slugs (mirrors hooks/useApps.ts)
const SPECIAL_CATEGORY_TRIGGERS = "_triggers";
const SPECIAL_CATEGORY_ACTIONS = "_actions";
const SPECIAL_CATEGORY_X402 = "_x402";

function isX402Action(action: ServerActionTriggerDef): boolean {
  return action.type.startsWith("x402ep_");
}

/**
 * Derive a single app from triggers, actions, and connector types.
 * Returns null if no triggers/actions match the slug.
 * Handles special category slugs (_triggers, _x402).
 */
export function deriveApp(
  slug: string,
  triggers: ServerActionTriggerDef[],
  actions: ServerActionTriggerDef[],
  connectorTypes: ServerConnectorType[],
): ServerApp | null {
  // Handle special category: Triggers
  if (slug === SPECIAL_CATEGORY_TRIGGERS) {
    if (triggers.length === 0) return null;
    return {
      slug: SPECIAL_CATEGORY_TRIGGERS,
      name: "Triggers",
      description: "All ways to start your workflows",
      triggerCount: triggers.length,
      actionCount: 0,
      totalIntegrations: triggers.length,
      isSpecialCategory: true,
    };
  }

  // Handle special category: Actions
  if (slug === SPECIAL_CATEGORY_ACTIONS) {
    if (actions.length === 0) return null;
    return {
      slug: SPECIAL_CATEGORY_ACTIONS,
      name: "Actions",
      description: "All actions you can use in your workflows",
      triggerCount: 0,
      actionCount: actions.length,
      totalIntegrations: actions.length,
      isSpecialCategory: true,
    };
  }

  // Handle special category: x402 Endpoints
  if (slug === SPECIAL_CATEGORY_X402) {
    const x402Actions = actions.filter(isX402Action);
    if (x402Actions.length === 0) return null;
    return {
      slug: SPECIAL_CATEGORY_X402,
      name: "x402 Endpoints",
      description: "Payment-gated API endpoints",
      triggerCount: 0,
      actionCount: x402Actions.length,
      totalIntegrations: x402Actions.length,
      isSpecialCategory: true,
    };
  }

  const appTriggers = triggers.filter(t => getAppSlug(t) === slug);
  const appActions = actions.filter(a => getAppSlug(a) === slug);

  if (appTriggers.length === 0 && appActions.length === 0) return null;

  // Find best matching connector type.
  const connector = connectorTypes.find(ct => {
    const ctSlug = normalizeToSlug(ct.type);
    return ctSlug === slug;
  });

  return {
    slug,
    name: connector?.name || formatName(slug),
    description: connector?.description || `Integrations for ${formatName(slug)}`,
    logoUrl: connector?.logoUrl,
    categories: connector?.categories,
    tags: connector?.tags,
    triggerCount: appTriggers.length,
    actionCount: appActions.length,
    totalIntegrations: appTriggers.length + appActions.length,
  };
}

/**
 * Get triggers for a specific app slug.
 * For the _triggers special category, returns all triggers.
 */
export function getTriggersForApp(slug: string, triggers: ServerActionTriggerDef[]): ServerActionTriggerDef[] {
  if (slug === SPECIAL_CATEGORY_TRIGGERS) return triggers;
  if (slug === SPECIAL_CATEGORY_ACTIONS) return [];
  if (slug === SPECIAL_CATEGORY_X402) return [];
  return triggers.filter(t => getAppSlug(t) === slug);
}

/**
 * Get actions for a specific app slug.
 * For the _actions special category, returns all actions.
 * For the _x402 special category, returns all x402 actions.
 */
export function getActionsForApp(slug: string, actions: ServerActionTriggerDef[]): ServerActionTriggerDef[] {
  if (slug === SPECIAL_CATEGORY_ACTIONS) return actions;
  if (slug === SPECIAL_CATEGORY_X402) return actions.filter(isX402Action);
  if (slug === SPECIAL_CATEGORY_TRIGGERS) return [];
  return actions.filter(a => getAppSlug(a) === slug);
}

/**
 * Get the connector logo URL for a given connector type.
 */
export function getConnectorLogoUrl(connectorType: string, connectorTypes: ServerConnectorType[]): string | undefined {
  const ct = connectorTypes.find(c => c.type === connectorType);
  return ct?.logoUrl;
}

const B3OS_ICON_CDN_BASE = "https://cdn.b3.fun/b3os-icons";

export function buildServerApps(
  triggers: ServerActionTriggerDef[],
  actions: ServerActionTriggerDef[],
  connectorTypes: ServerConnectorType[],
): ServerApp[] {
  const slugSet = new Set<string>();
  [...triggers, ...actions].forEach(item => {
    const slug = getAppSlug(item);
    if (slug) slugSet.add(slug);
  });

  const connectorBySlug = new Map<string, ServerConnectorType>();
  connectorTypes.forEach(ct => {
    const slug = normalizeToSlug(ct.type);
    const existing = connectorBySlug.get(slug);
    if (!existing || (existing.provider === "pipedream" && !ct.provider)) {
      connectorBySlug.set(slug, ct);
    }
  });

  const apps: ServerApp[] = Array.from(slugSet).map(slug => {
    const connector = connectorBySlug.get(slug);
    const appTriggers = triggers.filter(t => getAppSlug(t) === slug);
    const appActions = actions.filter(a => getAppSlug(a) === slug);
    return {
      slug,
      name: connector?.name || formatName(slug),
      description: connector?.description || `Integrations for ${formatName(slug)}`,
      logoUrl: connector?.logoUrl,
      categories: connector?.categories,
      tags: connector?.tags || connector?.categories?.map(c => c.toLowerCase().replace(/ /g, "-")),
      triggerCount: appTriggers.length,
      actionCount: appActions.length,
      totalIntegrations: appTriggers.length + appActions.length,
    };
  });

  const specialApps: ServerApp[] = [];
  if (triggers.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_TRIGGERS,
      name: "Triggers",
      description: "All ways to start your workflows",
      logoUrl: `${B3OS_ICON_CDN_BASE}/triggers.svg`,
      triggerCount: triggers.length,
      actionCount: 0,
      totalIntegrations: triggers.length,
      isSpecialCategory: true,
    });
  }
  if (actions.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_ACTIONS,
      name: "Actions",
      description: "All actions you can use in your workflows",
      logoUrl: `${B3OS_ICON_CDN_BASE}/actions.svg`,
      triggerCount: 0,
      actionCount: actions.length,
      totalIntegrations: actions.length,
      isSpecialCategory: true,
    });
  }
  const x402Actions = actions.filter(isX402Action);
  if (x402Actions.length > 0) {
    specialApps.push({
      slug: SPECIAL_CATEGORY_X402,
      name: "x402 Endpoints",
      description: "Open ecosystem endpoints",
      logoUrl: `${B3OS_ICON_CDN_BASE}/x402.svg`,
      triggerCount: 0,
      actionCount: x402Actions.length,
      totalIntegrations: x402Actions.length,
      isSpecialCategory: true,
    });
  }

  return [...specialApps, ...apps];
}

// -----------------------------------------------------------------------
// Templates
// -----------------------------------------------------------------------

export interface ServerTemplateTag {
  id: string;
  name: string;
  slug: string;
  imageUrl?: string;
  description?: string;
  weight: number;
  category: string;
}

export interface ServerTemplateNode {
  type: string;
  description?: string;
  children: string[];
  loopBody?: string[];
  connector?: { type: string };
}

export interface ServerTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  slug?: string;
  tags?: ServerTemplateTag[];
  definition?: { nodes: Record<string, ServerTemplateNode> };
  organizationName?: string;
  organizationPhoto?: string;
  organizationId?: string;
  visibility?: string;
  isPromoted?: boolean;
  forkCount?: number;
  version?: number;
  maxRuns?: number;
  cooldownMs?: number;
  archived?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

const TEMPLATES_LIST_CACHE = {
  next: { ...LONG_CACHE.next, tags: [TEMPLATES_CACHE_TAG] },
};

function templateDetailCache(templateIdOrSlug: string) {
  return {
    next: { ...LONG_CACHE.next, tags: [TEMPLATES_CACHE_TAG, templateCacheTag(templateIdOrSlug)] },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function scrubTemplateConnectorIds<T extends { definition?: { nodes?: Record<string, any> } }>(template: T): T {
  const nodes = template.definition?.nodes;
  if (!nodes) return template;
  const scrubbed: Record<string, unknown> = {};
  for (const [id, node] of Object.entries(nodes)) {
    if (node.connector?.id && !VARIABLE_REF_RE.test(node.connector.id)) {
      scrubbed[id] = { ...node, connector: { type: node.connector.type } };
    } else {
      scrubbed[id] = node;
    }
  }
  return { ...template, definition: { ...template.definition!, nodes: scrubbed } };
}

export async function fetchServerTemplates(
  category: "public" | "promoted" = "public",
  limit = 50,
): Promise<ServerTemplate[]> {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return [];

  try {
    const res = await fetch(
      `${endpoint}/v1/templates?category=${category}&limit=${limit}&offset=0`,
      TEMPLATES_LIST_CACHE,
    );
    if (!res.ok) return [];
    const body = await res.json();
    const data = body.data ?? body;
    const templates = (data as PaginatedResponse<ServerTemplate>).items ?? (data as ServerTemplate[]);
    return templates.filter(t => !t.archived).map(scrubTemplateConnectorIds);
  } catch {
    return [];
  }
}

export const fetchServerTemplate = cache(async function fetchServerTemplate(
  templateId: string,
): Promise<ServerTemplate | "archived" | null> {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return null;

  try {
    const res = await fetch(`${endpoint}/v1/templates/${templateId}`, templateDetailCache(templateId));
    if (res.status === 410) return "archived";
    if (!res.ok) return null;
    const body = await res.json();
    return scrubTemplateConnectorIds((body.data ?? body) as ServerTemplate);
  } catch {
    return null;
  }
});

// -----------------------------------------------------------------------
// Integration Definition (single action/trigger detail)
// -----------------------------------------------------------------------

export interface ServerIntegrationDef {
  type: string;
  name: string;
  description?: string;
  platform?: string;
  category?: string;
  tags?: string[];
  payloadSchema?: {
    type?: string;
    required?: string[];
    properties?: Record<string, { type?: string; description?: string; hidden?: boolean }>;
  };
  resultSchema?: {
    type?: string;
    required?: string[];
    properties?: Record<string, { type?: string; description?: string }>;
  };
  logoUrl?: string;
  connector?: { type: string };
}

function getIntegrationEndpoint(integrationType: string): string {
  switch (integrationType) {
    case "trigger":
    case "triggers":
      return "triggers";
    case "action":
    case "actions":
      return "actions";
    case "logic":
    case "logic-action":
    case "logic-actions":
      return "logic-actions";
    default:
      return "actions";
  }
}

/** Map an integrationType param to a display label ("Trigger" / "Logic" / "Action"). */
export function getIntegrationTypeLabel(integrationType: string): string {
  switch (integrationType) {
    case "trigger":
    case "triggers":
      return "Trigger";
    case "logic":
    case "logic-action":
    case "logic-actions":
      return "Logic";
    default:
      return "Action";
  }
}

/** Derive the canonical URL slug for an integration (slugified name, falling back to type). */
export function integrationSlug(def: { name?: string; type: string }): string {
  return (def.name && slugifyName(def.name)) || def.type;
}

/**
 * Format a slug/type into a display name (e.g. "send-email" → "Send Email").
 * NOTE: Duplicated in scripts/fetch-public-pages.ts (standalone script). Keep in sync.
 */
export function formatType(type: string): string {
  return type.replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Slugify a name to match URL slug format (e.g. "Get Weather Data" → "get-weather-data").
 * NOTE: Duplicated in scripts/fetch-public-pages.ts (standalone script). Keep in sync.
 */
export function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * When a slug-based URL doesn't match any action type directly,
 * fetch the full list and find the action whose slugified name matches.
 * Returns the matching definition or null.
 *
 * Note: This incurs an extra list fetch + detail fetch on cache miss.
 * The LONG_CACHE preset mitigates this for repeated visits.
 * A dedicated backend slug-resolution endpoint would eliminate this overhead.
 */
async function resolveIntegrationBySlug(
  endpoint: string,
  apiEndpoint: string,
  slug: string,
): Promise<ServerIntegrationDef | null> {
  try {
    // Use CACHE_ENDPOINT for the list (matches client-side useActions/useTriggers)
    const listEndpoint = CACHE_ENDPOINT || endpoint;
    const res = await fetch(`${listEndpoint}/v1/${apiEndpoint}`, LONG_CACHE);
    if (!res.ok) return null;
    const body = await res.json();
    const items = (body.data ?? body) as { type: string; name?: string }[];
    if (!Array.isArray(items)) return null;

    const matches = items.filter(item => item.name && slugifyName(item.name) === slug);
    if (matches.length === 0) return null;

    if (matches.length > 1) {
      console.warn(
        `[resolveIntegrationBySlug] Slug collision: "${slug}" matches ${matches.length} items: ${matches.map(m => m.type).join(", ")}. Using first match.`,
      );
    }

    // Fetch the full definition by the resolved type
    const defRes = await fetch(`${endpoint}/v1/${apiEndpoint}/${matches[0].type}`, LONG_CACHE);
    if (!defRes.ok) return null;
    const defBody = await defRes.json();
    return (defBody.data ?? defBody ?? null) as ServerIntegrationDef;
  } catch {
    return null;
  }
}

// Wrapped with React cache() to deduplicate calls within a single server render
// (layout.tsx + page.tsx both call this with the same args).
export const fetchServerIntegrationDef = cache(async function fetchServerIntegrationDef(
  integrationType: string,
  type: string,
): Promise<ServerIntegrationDef | null> {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return null;
  const apiEndpoint = getIntegrationEndpoint(integrationType);
  try {
    const res = await fetch(`${endpoint}/v1/${apiEndpoint}/${type}`, LONG_CACHE);
    if (res.ok) {
      const body = await res.json();
      return (body.data ?? body ?? null) as ServerIntegrationDef;
    }
    // Direct lookup failed — try resolving as a human-readable slug
    return await resolveIntegrationBySlug(endpoint, apiEndpoint, type);
  } catch {
    return null;
  }
});

// -----------------------------------------------------------------------
// x402 endpoint fetchers (used by /integrations/x402/* routes + sitemap)
// -----------------------------------------------------------------------

interface X402ListResponse {
  items: X402Endpoint[];
  hasMore: boolean;
  limit: number;
  offset: number;
}

/** Fetches all public x402 endpoints (revalidates every 2 minutes). Returns [] on error. */
export const fetchServerX402Endpoints = cache(async function fetchServerX402Endpoints(): Promise<X402Endpoint[]> {
  const endpoint = requireV2Endpoint();
  if (!endpoint) return [];

  const PAGE_SIZE = 100; // API caps limit at 100
  const MAX_PAGES = 200; // hard cap → 20k endpoints
  const all: X402Endpoint[] = [];
  let offset = 0;

  for (let page = 0; page < MAX_PAGES; page++) {
    try {
      const res = await fetch(`${endpoint}/v1/x402-endpoints?limit=${PAGE_SIZE}&offset=${offset}`, MEDIUM_CACHE);
      if (!res.ok) {
        console.warn(`[server-fetchers] x402 list ${res.status} ${res.statusText}`);
        break;
      }
      const body = await res.json();
      const data = (body.data ?? body) as X402ListResponse;
      const items = data.items ?? [];
      all.push(...items);
      if (!data.hasMore || items.length === 0) return all;
      offset += PAGE_SIZE;
    } catch (err) {
      console.warn("[server-fetchers] x402 list fetch failed:", (err as Error).message);
      break;
    }
  }

  console.warn(`[server-fetchers] x402 list truncated at ${MAX_PAGES * PAGE_SIZE} endpoints`);
  return all;
});

/** Fetches a single x402 endpoint by 6-char shortId. Returns null if missing. */
export const fetchServerX402Endpoint = cache(async function fetchServerX402Endpoint(
  shortId: string,
): Promise<X402Endpoint | null> {
  if (!shortId) return null;
  const all = await fetchServerX402Endpoints();
  return all.find(e => shortIdOf(e.id) === shortId) ?? null;
});
